import { QueryClient, QueryKey } from "@tanstack/react-query";

import { removeNullUndefined } from "@nens/shared-utils";

import { ItemUpdaterProps } from "./types/update-item-props";
import { defaultGetItemId } from "./utils/default-get-item-id";

// Describes how the items are stored in a list response, so the updater can work with any list shape.
type ListAdapter<TItem, TList> = {
  getItems: (list: TList) => TItem[];
  setItems: (list: TList, items: TItem[]) => TList;
};

type GenericProps<TItem, TUpdate, TId extends string | number> = {
  getQueryKey: (id: TId) => QueryKey;
  getItemId: (item: TItem | TUpdate) => TId | undefined;
};

/**
 * Generic core for the list updaters. The variants below only differ in the shape of the list response
 * and the type of the id (same pattern as `fetchAllPagesGeneric`).
 */
function createItemUpdaterForQueryListsGeneric<
  TItem,
  TUpdate extends Partial<TItem>,
  TId extends string | number,
  TList
>(
  queryClient: QueryClient,
  { getQueryKey, getItemId }: GenericProps<TItem, TUpdate, TId>,
  { getItems, setItems }: ListAdapter<TItem, TList>
) {
  return async (beforeId: TId, updatedItem: TUpdate | null) => {
    const id = (updatedItem && getItemId(updatedItem)) ?? beforeId;

    const updateItemsInLists = (id: TId) => {
      const queryKey = getQueryKey(id);

      const allQueries = queryClient.getQueryCache().findAll({
        queryKey,
        exact: false,
      });

      allQueries.forEach(({ queryKey }) => {
        const oldQuery = queryClient.getQueryData<TList>(queryKey);
        const oldItems = oldQuery && getItems(oldQuery);
        if (!oldQuery || !oldItems) return;

        let foundUpdate = false;

        const newItems = removeNullUndefined(
          oldItems.map((oldItem) => {
            if (foundUpdate || getItemId(oldItem) !== id) return oldItem;
            foundUpdate = true;
            if (!updatedItem) return null;

            // If the updateItem has a different id remove the old item. (happens when moving files for example)
            const itemId = getItemId(updatedItem);
            if (itemId && id !== itemId) return null;

            return { ...oldItem, ...updatedItem };
          })
        );

        if (foundUpdate) queryClient.setQueryData<TList>(queryKey, setItems(oldQuery, newItems));
      });
    };

    // Handle move (id changed)
    if (id !== beforeId) {
      // Update item in lists with old id (removes it)
      updateItemsInLists(beforeId);

      // Invalidate all lists with new id (so it gets refetched and the item is added to the list(s) again)
      const queryKey = getQueryKey(id);
      await queryClient.invalidateQueries({
        queryKey,
      });

      return;
    }

    updateItemsInLists(id);
  };
}

/**
 * Factory function that creates an updater for items within all relevant list queries in the React Query cache.
 * Lists are shaped like `{ items: TItem[] }`.
 */
export function createItemUpdaterForQueryLists<
  TItem = { id: string },
  TUpdate extends Partial<TItem> = Partial<TItem>
>(
  queryClient: QueryClient,
  { getQueryKey, getItemId = defaultGetItemId }: ItemUpdaterProps<TItem, TUpdate>
) {
  return createItemUpdaterForQueryListsGeneric<TItem, TUpdate, string, { items: TItem[] }>(
    queryClient,
    { getQueryKey, getItemId },
    {
      getItems: (list) => list.items,
      setItems: (list, items) => ({ ...list, items }),
    }
  );
}

/**
 * HCC variant of `createItemUpdaterForQueryLists`. Lists are shaped like `{ count: number; results: TItem[] }`
 * and ids are numeric. The `count` is kept in sync when an item is removed from a list.
 */
export function createHccItemUpdaterForQueryLists<
  TItem,
  TUpdate extends Partial<TItem> = Partial<TItem>
>(
  queryClient: QueryClient,
  { getQueryKey, getItemId = defaultGetItemId<number> }: ItemUpdaterProps<TItem, TUpdate, number>
) {
  return createItemUpdaterForQueryListsGeneric<
    TItem,
    TUpdate,
    number,
    { count: number; results: TItem[] }
  >(
    queryClient,
    { getQueryKey, getItemId },
    {
      getItems: (list) => list.results,
      setItems: (list, results) => ({
        ...list,
        results,
        count: list.count - (list.results.length - results.length),
      }),
    }
  );
}
